/**
 * "Click to continue": the one thing a browser makes a mouse player do.
 *
 * Pointer lock can only be requested from a real user gesture. Every panel in
 * the game releases the mouse so it can be clicked, so when a panel closes the
 * mouse is free and something has to ask for it back. That is all this is now:
 * a small card in the middle of the screen, and a click on it takes the mouse.
 *
 * It used to be the pause screen too, because Escape releasing the mouse was
 * the pause button. The pause menu (`menus.js`) is its own screen now: losing
 * the lock while nothing asked for it (which is Escape) calls `onPause`.
 *
 * A pad never needs it. A pad player closing a panel goes straight back to the
 * game, and the card never shows while the pad is the device in use.
 */
import { glyph } from './glyphs.js';

export function createVeil({ parent, input, onPause = null }) {
  const root = document.createElement('div');
  root.className = 'veil';
  root.hidden = true;
  root.innerHTML = `
    <div class="veil-card">
      <span class="veil-title">Click to continue</span>
      <span class="veil-sub">${glyph('[Esc] pauses')}</span>
    </div>`;
  root.addEventListener('click', () => input.requestLock());
  parent.append(root);

  // A modal releases pointer lock on purpose, and that is not a pause.
  let suppressed = false;

  const wanted = () => !suppressed && !input.locked && input.device !== 'gamepad';

  input.onLockChange((locked) => {
    if (locked) {
      root.hidden = true;
      return;
    }
    // Lost while nothing asked for it: the player pressed Escape.
    if (!suppressed) onPause?.();
    root.hidden = !wanted();
  });
  input.onDeviceChange?.(() => {
    root.hidden = !wanted() || root.hidden;
  });

  return {
    root,
    show() {
      root.hidden = !wanted();
    },
    hide() {
      root.hidden = true;
    },
    /** Hides the card while something else owns the screen. */
    suppress(value) {
      suppressed = value;
      if (value) root.hidden = true;
      else root.hidden = !wanted();
    },
    get suppressed() {
      return suppressed;
    },
  };
}
